import { memo, useState } from "react";
import { Check, Pencil, CalendarClock } from "lucide-react";
import { fmtDay, fmtShortDate, dayWerkelijk, extraGroup, outsideTotal } from "./budgetUtils";
import { ExtraEditForm } from "./ExtraEditForm";

const ExtraChips = ({ items, editId, setEditId }) => (
  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
    {items.map((e) => (
      <button key={e.id} type="button" data-testid={`day-extra-edit-${e.id}`} onClick={() => setEditId(editId === e.id ? null : e.id)}
        title="Klik om deze onkost aan te passen"
        className="group flex items-center gap-1.5 rounded-full bg-[#B5838D]/10 hover:bg-[#B5838D]/20 text-[#8a5a66] text-[11px] font-semibold px-2.5 py-1 transition-[background-color] duration-150">
        <span className="truncate max-w-[180px]">{e.label}</span>
        {e.date && <span className="opacity-70 font-normal">{fmtShortDate(e.date)}</span>}
        <span className="font-mono">€{e.amount_eur.toFixed(2)}</span>
        <Pencil className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity duration-150" />
      </button>
    ))}
  </div>
);

const OutsideSection = ({ kind, items, nDays, tripId, members, expenses, perDay, editId, setEditId, onSaved }) => {
  if (!items.length) return null;
  const total = items.reduce((a, e) => a + (e.amount_eur || 0), 0);
  const editItem = items.find((x) => x.id === editId);
  return (
    <div data-testid={`budget-${kind}-section`} className="border-b border-border/60 py-2 bg-amber-50/50 -mx-2 px-2 rounded-lg">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1.5 min-w-0">
          <CalendarClock className="w-3.5 h-3.5 text-[#946200] shrink-0" />
          <span className="truncate font-semibold">{kind === "vooraf" ? "Vooraf" : "Achteraf"} <span className="text-xs font-normal text-muted-foreground">· {kind === "vooraf" ? "vóór vertrek (boekingen, ferry, vignet...)" : "na de reis"}</span></span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {items.some((e) => e.split_expense_id) && (
            <span data-testid={`budget-${kind}-split-badge`} title="Deze kosten zijn (deels) verdeeld met reisgenoten en tellen mee in de saldo's van 'Wie betaalt wat'"
              className="text-[10px] font-bold uppercase tracking-wide text-[#3A6EA5] bg-[#3A6EA5]/10 rounded-full px-2 py-0.5">verdeeld</span>
          )}
          <span data-testid={`budget-${kind}-total`} className="font-mono font-bold text-sm">€ {total.toFixed(2)}</span>
        </span>
      </div>
      {nDays > 0 && (
        <p className="text-[11px] text-muted-foreground font-mono mt-0.5">uitgesmeerd over {nDays} dagen: +€ {(total / nDays).toFixed(2)}/dag bij "werkelijk"</p>
      )}
      <ExtraChips items={items} editId={editId} setEditId={setEditId} />
      {editItem && (
        <ExtraEditForm tripId={tripId} item={editItem} splitExpense={(expenses || []).find((x) => x.id === editItem.split_expense_id)}
          members={members} perDay={perDay} onCancel={() => setEditId(null)} onSaved={onSaved} />
      )}
    </div>
  );
};

export const PerDayList = memo(({ tripId, perDay, extras, dailyBudget, travelMode, members, expenses, onRefresh }) => {
  const [editId, setEditId] = useState(null);
  const nDays = perDay?.length || 0;
  const byDate = (a, b) => (a.date || "").localeCompare(b.date || "");
  const vooraf = extras.filter((e) => extraGroup(e, perDay) === "vooraf").sort(byDate);
  const achteraf = extras.filter((e) => extraGroup(e, perDay) === "achteraf").sort(byDate);
  const spread = nDays ? outsideTotal(extras, perDay) / nDays : 0;
  const onSaved = () => { setEditId(null); window.dispatchEvent(new Event("vr-split-refresh")); onRefresh?.(); };
  return (
    <div className="bg-card rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <p className="font-heading font-bold text-sm">Per dag</p>
        <p className="text-[11px] text-muted-foreground">Onkosten met een datum of dag verschijnen hier automatisch</p>
      </div>
      <div className="space-y-1">
        <OutsideSection kind="vooraf" items={vooraf} nDays={nDays} tripId={tripId} members={members} expenses={expenses}
          perDay={perDay} editId={editId} setEditId={setEditId} onSaved={onSaved} />
        {perDay?.map((d) => {
          const dayExtras = extras.filter((e) => e.day_number === d.day_number);
          const wk = dayWerkelijk(d, extras, spread);
          const kwhDay = dayExtras.reduce((a, e) => a + (e.kwh || 0), 0);
          const over = dailyBudget ? (wk ?? d.total) > dailyBudget : null;
          const editItem = dayExtras.find((x) => x.id === editId);
          return (
            <div key={d.day_number} data-testid={`budget-day-${d.day_number}`} className="border-b border-border/60 last:border-0 py-2">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5 min-w-0">
                  {over != null && <span className={`w-2 h-2 rounded-full shrink-0 ${over ? "bg-red-500" : "bg-[#52B788]"}`} />}
                  <span className="truncate">Dag {d.day_number} <span className="text-xs">· {fmtDay(d.date)}</span></span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`font-mono text-xs ${wk != null ? "line-through text-muted-foreground/60" : "font-semibold text-sm"}`}>€ {d.total.toFixed(2)}</span>
                  {wk != null && (
                    <>
                      <span data-testid={`actual-check-${d.day_number}`} title={`Werkelijk bedrag o.b.v. jouw onkosten: per categorie vervangen je ingaves de raming, categorieën zonder ingaves tellen als raming mee${spread > 0 ? ` — plus € ${spread.toFixed(2)}/dag uitgesmeerde kosten van vooraf/achteraf` : ""}`}
                        className="w-4 h-4 rounded-full bg-[#2D6A4F] flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
                      </span>
                      <span data-testid={`actual-total-${d.day_number}`} className={`font-mono font-bold ${wk <= d.total ? "text-[#2D6A4F]" : "text-red-600"}`}>€ {wk.toFixed(2)}</span>
                    </>
                  )}
                  {dayExtras.some((e) => e.split_expense_id) && (
                    <span data-testid={`day-split-badge-${d.day_number}`} title="Onkosten van deze dag zijn (deels) verdeeld met reisgenoten"
                      className="text-[10px] font-bold uppercase tracking-wide text-[#3A6EA5] bg-[#3A6EA5]/10 rounded-full px-2 py-0.5 shrink-0">verdeeld</span>
                  )}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                raming: {travelMode === "moto" ? "brandstof" : "laden"} €{(d.charging + d.overnight_charging).toFixed(0)} · slapen €{d.stay.toFixed(0)} · eten €{d.food.toFixed(0)}{(d.tolls || 0) > 0 && <> · tol €{d.tolls.toFixed(0)}</>}
                {spread > 0 && <> · vooraf/achteraf €{spread.toFixed(2)}</>}
                {kwhDay > 0 && <> — geladen: {Math.round(kwhDay * 10) / 10} kWh</>}
              </p>
              {dayExtras.length > 0 && <ExtraChips items={dayExtras} editId={editId} setEditId={setEditId} />}
              {editItem && (
                <ExtraEditForm tripId={tripId} item={editItem} splitExpense={(expenses || []).find((x) => x.id === editItem.split_expense_id)}
                  members={members} perDay={perDay} onCancel={() => setEditId(null)} onSaved={onSaved} />
              )}
            </div>
          );
        })}
        <OutsideSection kind="achteraf" items={achteraf} nDays={nDays} tripId={tripId} members={members} expenses={expenses}
          perDay={perDay} editId={editId} setEditId={setEditId} onSaved={onSaved} />
      </div>
    </div>
  );
});
